
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Table from '../../../components/docs/Table';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  layout: 'reference',
  source: 'endpoint',
  description: 'A comprehensive breakdown of all Plaid error codes'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Error = makeShortcode("Error");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Errors' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "most-common-errors"
    }}>{`Most common errors`}</h4>
    <p>{`The following are the most common errors that may occur in response to an API call even if your implementation is correct. This list of common errors excludes Item errors that occur only during the Link flow (typically due to bad data entry by the end user), such as `}<a parentName="p" {...{
        "href": "/docs/errors/item/#invalid_credentials"
      }}><inlineCode parentName="a">{`INVALID_CREDENTIALS`}</inlineCode></a>{`, and errors that can occur only due to sending invalid input, such as `}<a parentName="p" {...{
        "href": "/docs/errors/invalid-request/#invalid_field"
      }}><inlineCode parentName="a">{`INVALID_FIELD`}</inlineCode></a>{`. It is recommended that your integration should, at minimum, handle each of the errors below that are applicable to your product and/or integration mode.`}</p>
    <p>{`In the table below, "institution-based products" refers to any product or integration that connects to a bank or other financial institution (i.e., most Plaid products); it excludes products such as Identity Verification, Monitor, Enrich, and Document Income that do not involve making a connection to a financial institution.`}</p>
    <Layout.FullWidth>
      <div className="errors-overview-table">
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Error`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Applies to`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Summary`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#item_login_required"
                }}><inlineCode parentName="a">{`ITEM_LOGIN_REQUIRED`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All institution-based products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Item has expired credentials or consent`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#product_not_ready"
                }}><inlineCode parentName="a">{`PRODUCT_NOT_READY`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Signal, Assets, Income, Check, Auth, `}<inlineCode parentName="td">{`/transactions/get`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Plaid hasn't finished obtaining the data needed to fulfill your request`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#products_not_supported"
                }}><inlineCode parentName="a">{`PRODUCTS_NOT_SUPPORTED`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All institution-based products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The product endpoint isn't compatible with this Item`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/transactions/#transactions_sync_mutation_during_pagination"
                }}><inlineCode parentName="a">{`TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`/transactions/sync`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`An update was received during Transactions pagination`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#no_accounts"
                }}><inlineCode parentName="a">{`NO_ACCOUNTS`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All institution-based products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Couldn't find any open accounts`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#no_auth_accounts-or-no-depository-accounts"
                }}><inlineCode parentName="a">{`NO_AUTH_ACCOUNTS`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Auth`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Couldn't find any debitable checking, savings, or cash management accounts`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#no_liability_accounts"
                }}><inlineCode parentName="a">{`NO_LIABILITY_ACCOUNTS`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Liabilities`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Couldn't find any credit accounts`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#no_investment_accounts"
                }}><inlineCode parentName="a">{`NO_INVESTMENT_ACCOUNTS`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Investments`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Couldn't find any investment accounts`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/item/#access_not_granted"
                }}><inlineCode parentName="a">{`ACCESS_NOT_GRANTED`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All institution-based products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The end user didn't grant an OAuth permission required for your request`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/invalid-input/#additional_consent_required"
                }}><inlineCode parentName="a">{`ADDITIONAL_CONSENT_REQUIRED`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Integrations in the US or Canada that `}<a parentName="td" {...{
                  "href": "/docs/link/initializing-products/#adding-products-post-link"
                }}>{`add products to Items after Link`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The end user didn't grant a data scope required for your request`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/institution/#institution_not_responding"
                }}><inlineCode parentName="a">{`INSTITUTION_NOT_RESPONDING`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All institution-based products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Temporary financial institution connectivity outage`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/institution/#institution_down"
                }}><inlineCode parentName="a">{`INSTITUTION_DOWN`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All institution-based products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Temporary financial institution connectivity outage`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/rate-limit-exceeded/"
                }}><inlineCode parentName="a">{`RATE_LIMIT_EXCEEDED`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Applications that batch-process Plaid API calls or have heavy traffic spikes`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Too many requests made too quickly, or API usage caps hit`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/docs/errors/api/#internal_server_error-or-plaid-internal-error"
                }}><inlineCode parentName="a">{`INTERNAL_SERVER_ERROR`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`All products`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Internal error or financial institution error not otherwise specified`}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Layout.FullWidth>
    <h4>Errors overview</h4>
    <Layout.FullWidth>
      <Table.Container>
        <Table.Row title='Item errors' subtitle="Occur when an Item may be invalid or not supported on Plaid's platform." columns={3} //there are too many errors to list all in one data column
        >
  <Error type='item' name='ACCESS_NOT_GRANTED' mdxType="Error" />
  <Error type='item' name='INSTANT_MATCH_FAILED' mdxType="Error" />
  <Error type='item' name='INSUFFICIENT_CREDENTIALS' mdxType="Error" />
  <Error type='item' name='INVALID_CREDENTIALS' mdxType="Error" />
  <Error type='item' name='INVALID_MFA' mdxType="Error" />
  <Error type='item' name='INVALID_OTP' mdxType="Error" />
  <Error type='item' name='INVALID_PHONE_NUMBER' mdxType="Error" />
  <Error type='item' name='INVALID_SEND_METHOD' mdxType="Error" />
  <Error type='item' name='INVALID_UPDATED_USERNAME' mdxType="Error" />
  <Error type='item' name='ITEM_CONCURRENTLY_DELETED' mdxType="Error" />
  <Error type='item' name='ITEM_LOCKED' mdxType="Error" />
  <Error type='item' name='ITEM_LOGIN_REQUIRED' mdxType="Error" />
  <Error type='item' name='ITEM_NOT_FOUND' mdxType="Error" />
  <Error type='item' name='ITEM_NOT_SUPPORTED' mdxType="Error" />
  <Error type='item' name='MANUAL_VERIFICATION_REQUIRED' mdxType="Error" />
  <Error type='item' name='MFA_NOT_SUPPORTED' mdxType="Error" />
  <Error type='item' name='NO_ACCOUNTS' mdxType="Error" />
  <Error type='item' url='#no_auth_accounts-or-no-depository-accounts' name='NO_AUTH_ACCOUNTS' description='or no-depository-accounts' mdxType="Error" />
  <Error type='item' name='NO_ELIGIBLE_ITEMS' mdxType="Error" />
  <Error type='item' name='NO_INVESTMENT_ACCOUNTS' mdxType="Error" />
  <Error type='item' name='NO_LIABILITY_ACCOUNTS' mdxType="Error" />
  <Error type='item' name='PASSWORD_RESET_REQUIRED' mdxType="Error" />
  <Error type='item' name='PRODUCT_NOT_ENABLED' mdxType="Error" />
  <Error type='item' name='PRODUCT_NOT_READY' mdxType="Error" />
  <Error type='item' name='PRODUCTS_NOT_SUPPORTED' mdxType="Error" />
  <Error type='item' name='USER_INPUT_TIMEOUT' mdxType="Error" />
  <Error type='item' name='USER_SETUP_REQUIRED' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Institution errors' subtitle='Occur when there are errors for the requested financial institution.' columns={3}>
  <Error type='institution' name='INSTITUTION_DOWN' mdxType="Error" />
  <Error type='institution' name='INSTITUTION_NO_LONGER_SUPPORTED' mdxType="Error" />
  <Error type='institution' name='INSTITUTION_NOT_AVAILABLE' mdxType="Error" />
  <Error type='institution' name='INSTITUTION_NOT_ENABLED_IN_ENVIRONMENT' mdxType="Error" />
  <Error type='institution' name='INSTITUTION_NOT_RESPONDING' mdxType="Error" />
  <Error type='institution' name='INSTITUTION_REGISTRATION_REQUIRED' mdxType="Error" />
  <Error type='institution' name='UNAUTHORIZED_INSTITUTION' mdxType="Error" />
  <Error type='institution' name='UNSUPPORTED_RESPONSE' mdxType="Error" />
        </Table.Row>
        <Table.Row title='API errors' subtitle='Occur during planned maintenance and in response to API errors.' columns={3}>
  <Error type='api' url='#internal_server_error-or-plaid-internal-error' name='INTERNAL_SERVER_ERROR' description='or plaid-internal-error' mdxType="Error" />
  <Error type='api' name='PLANNED_MAINTENANCE' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Assets errors' subtitle='Occur for errors related to Asset endpoints.' columns={3}>
  <Error type='assets' name='PRODUCT_NOT_ENABLED' mdxType="Error" />
  <Error type='assets' name='DATA_UNAVAILABLE' mdxType="Error" />
  <Error type='assets' name='PRODUCT_NOT_READY' mdxType="Error" />
  <Error type='assets' name='ASSET_REPORT_GENERATION_FAILED' mdxType="Error" />
  <Error type='assets' name='INVALID_PARENT' mdxType="Error" />
  <Error type='assets' name='INSIGHTS_PREVIOUSLY_NOT_ENABLED' mdxType="Error" />
  <Error type='assets' name='DATA_QUALITY_CHECK_FAILED' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Payment errors' subtitle='Occur for errors related to Payment Initiation endpoints.' columns={3}>
  <Error type='payment' name='PAYMENT_BLOCKED' mdxType="Error" />
  <Error type='payment' name='PAYMENT_CANCELLED' mdxType="Error" />
  <Error type='payment' name='PAYMENT_INSUFFICIENT_FUNDS' mdxType="Error" />
  <Error type='payment' name='PAYMENT_INVALID_RECIPIENT' mdxType="Error" />
  <Error type='payment' name='PAYMENT_INVALID_REFERENCE' mdxType="Error" />
  <Error type='payment' name='PAYMENT_INVALID_SCHEDULE' mdxType="Error" />
  <Error type='payment' name='PAYMENT_REJECTED' mdxType="Error" />
  <Error type='payment' name='PAYMENT_SCHEME_NOT_SUPPORTED' mdxType="Error" />
  <Error type='payment' name='PAYMENT_CONSENT_INVALID_CONSTRAINTS' mdxType="Error" />
  <Error type='payment' name='PAYMENT_CONSENT_CANCELLED' mdxType="Error" />
        </Table.Row>
        <Table.Row title="Virtual Account errors" subtitle="Occur for errors related to Virtual Account endpoints." columns={3}>
  <Error type="virtual-account" name="TRANSACTION_INSUFFICIENT_FUNDS" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_AMOUNT_EXCEEDED" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_ON_SAME_ACCOUNT" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_CURRENCY_MISMATCH" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_IBAN_INVALID" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_BACS_INVALID" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_FAST_PAY_DISABLED" mdxType="Error" />
  <Error type="virtual-account" name="TRANSACTION_EXECUTION_FAILED" mdxType="Error" />
  <Error type="virtual-account" name="NONIDENTICAL_REQUEST" mdxType="Error" />
  <Error type="virtual-account" name="REQUEST_CONFLICT" mdxType="Error" />
        </Table.Row>
        <Table.Row title='Transactions errors' subtitle='Occur for errors related to Transactions endpoints.' columns={3}>
  <Error type='transactions' name='TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION' mdxType="Error" />
  <Error type='transactions' name='TRANSACTIONS_SYNC_ITEM_LIMIT_EXCEEDED' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Transfer errors' subtitle='Occur for errors related to Transfer endpoints.' columns={3}>
  <Error type='transfer' name='TRANSFER_NETWORK_LIMIT_EXCEEDED' mdxType="Error" />
  <Error type='transfer' name='TRANSFER_ACCOUNT_BLOCKED' mdxType="Error" />
  <Error type='transfer' name='TRANSFER_NOT_CANCELLABLE' mdxType="Error" />
  <Error type='transfer' name='TRANSFER_UNSUPPORTED_ACCOUNT_TYPE' mdxType="Error" />
  <Error type='transfer' name='TRANSFER_FORBIDDEN_ACH_CLASS' mdxType="Error" />
  <Error type='transfer' name='TRANSFER_ORIGINATOR_NOT_FOUND' mdxType="Error" />
  <Error type='transfer' name='INCOMPLETE_CUSTOMER_ONBOARDING' mdxType="Error" />
  <Error type='transfer' name='UNAUTHORIZED_ACCESS' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Signal errors' subtitle='Occur for errors related to Signal endpoints.' columns={3}>
  <Error type='signal' name='CLIENT_TRANSACTION_ID_ALREADY_IN_USE' mdxType="Error" />
  <Error type='signal' name='INVALID_CONFIGURATION_STATE' mdxType="Error" />
  <Error type='signal' name='NOT_ENABLED_FOR_SIGNAL_TRANSACTION_SCORE_RULESETS' mdxType="Error" />
  <Error type='signal' name='RULESET_NOT_FOUND' mdxType="Error" />
  <Error type='signal' name='SIGNAL_TRANSACTION_NOT_INITIATED' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Income errors' subtitle='Occur for errors related to Income endpoints.' columns={3}>
  <Error type='income' name='INCOME_VERIFICATION_DOCUMENT_NOT_FOUND' mdxType="Error" />
  <Error type='income' name='INCOME_VERIFICATION_FAILED' mdxType="Error" />
  <Error type='income' name='INCOME_VERIFICATION_NOT_FOUND' mdxType="Error" />
  <Error type='income' name='INCOME_VERIFICATION_UPLOAD_ERROR' mdxType="Error" />
  <Error type='income' name='PRODUCT_NOT_READY' mdxType="Error" />
  <Error type='income' name='EMPLOYMENT_NOT_FOUND' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Sandbox errors' subtitle='Occur when invalid parameters are supplied in the Sandbox environment.' columns={3}>
  <Error type='sandbox' name='SANDBOX_PRODUCT_NOT_ENABLED' mdxType="Error" />
  <Error type='sandbox' name='SANDBOX_WEBHOOK_INVALID' mdxType="Error" />
  <Error type='sandbox' name='SANDBOX_TRANSFER_EVENT_TRANSITION_INVALID' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Invalid Request errors' subtitle='Occur when a request is malformed and cannot be processed.' columns={3}>
  <Error type='invalid-request' name='MISSING_FIELDS' mdxType="Error" />
  <Error type='invalid-request' name='UNKNOWN_FIELDS' mdxType="Error" />
  <Error type='invalid-request' name='INVALID_FIELD' mdxType="Error" />
  <Error type='invalid-request' name='INVALID_CONFIGURATION' mdxType="Error" />
  <Error type='invalid-request' name='INCOMPATIBLE_API_VERSION' mdxType="Error" />
  <Error type='invalid-request' name='INVALID_BODY' mdxType="Error" />
  <Error type='invalid-request' name='INVALID_HEADERS' mdxType="Error" />
  <Error type='invalid-request' name='NOT_FOUND' mdxType="Error" />
  <Error type='invalid-request' name='NO_LONGER_AVAILABLE' mdxType="Error" />
  <Error type='invalid-request' name='SANDBOX_ONLY' mdxType="Error" />
  <Error type='invalid-request' name='INVALID_ACCOUNT_NUMBER' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Invalid Input errors' subtitle='Occur when all fields are provided, but the values provided are incorrect in some way.' columns={3}>
  <Error type='invalid-input' name='ADDITIONAL_CONSENT_REQUIRED' mdxType="Error" />
  <Error type='invalid-input' name='INCORRECT_DEPOSIT_VERIFICATION' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_ACCESS_TOKEN' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_ACCOUNT_ID' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_API_KEYS' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_AUDIT_COPY_TOKEN' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_INSTITUTION' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_LINK_CUSTOMIZATION' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_PROCESSOR_TOKEN' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_PRODUCT' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_PUBLIC_TOKEN' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_LINK_TOKEN' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_STRIPE_ACCOUNT' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_USER_ID' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_USER_IDENTITY_DATA' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_USER_TOKEN' mdxType="Error" />
  <Error type='invalid-input' name='INVALID_WEBHOOK_VERIFICATION_KEY_ID' mdxType="Error" />
  <Error type='invalid-input' name='PROFILE_ACCESS_FORBIDDEN' mdxType="Error" />
  <Error type='invalid-input' name='PROFILE_AUTHENTICATION_FAILED' mdxType="Error" />
  <Error type='invalid-input' name='UNAUTHORIZED_ENVIRONMENT' mdxType="Error" />
  <Error type='invalid-input' name='UNAUTHORIZED_ROUTE_ACCESS' mdxType="Error" />
  <Error type='invalid-input' name='USER_PERMISSION_REVOKED' mdxType="Error" />
  <Error type='invalid-input' name='TOO_MANY_VERIFICATION_ATTEMPTS' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Invalid Result errors' subtitle='Occur when a request is valid, but the output would be unusable for any supported flow.' columns={3}>
  <Error type='invalid-result' name='PLAID_DIRECT_ITEM_IMPORT_RETURNED_INVALID_MFA' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Rate Limit Exceeded errors' subtitle='Occur when an excessive number of requests are made in a short period of time.' columns={3}>
  <Error type='rate-limit-exceeded' name='ACCOUNTS_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='ACCOUNTS_BALANCE_GET_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='AUTH_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='BALANCE_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='CREDITS_EXHAUSTED' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='IDENTITY_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='INSTITUTIONS_GET_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='INSTITUTIONS_GET_BY_ID_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='INSTITUTION_RATE_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='INVESTMENTS_HOLDINGS_GET_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='INVESTMENTS_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='ITEM_GET_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='RATE_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='TRANSACTIONS_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='TRANSACTIONS_REFRESH_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='TRANSACTIONS_SYNC_LIMIT' mdxType="Error" />
  <Error type='rate-limit-exceeded' name='TRIAL_CONNECTION_LIMIT' mdxType="Error" />
        </Table.Row>
        <Table.Row title='ReCAPTCHA errors' subtitle='Occur when a ReCAPTCHA challenge has been presented or failed during the link process.' columns={3}>
  <Error type='recaptcha' name='RECAPTCHA_REQUIRED' mdxType="Error" />
  <Error type='recaptcha' name='RECAPTCHA_BAD' mdxType="Error" />
        </Table.Row>
        <Table.Row title='OAuth errors' subtitle='Occur when there is an error in OAuth authentication.' columns={3}>
  <Error type='oauth' name='INCORRECT_OAUTH_NONCE' mdxType="Error" />
  <Error type='oauth' name='INCORRECT_LINK_TOKEN' mdxType="Error" />
  <Error type='oauth' name='OAUTH_STATE_ID_ALREADY_PROCESSED' mdxType="Error" />
  <Error type='oauth' name='OAUTH_STATE_ID_NOT_FOUND' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Micro-deposits errors' subtitle='Occur when there is an error with micro-deposits.' columns={3}>
  <Error type='microdeposits' name='BANK_TRANSFER_ACCOUNT_BLOCKED' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Partner errors' subtitle='Occur when there is an error with creating or managing end customers.' columns={3}>
  <Error type='partner' name='CUSTOMER_NOT_FOUND' mdxType="Error" />
  <Error type='partner' name='FLOWDOWN_NOT_COMPLETE' mdxType="Error" />
  <Error type='partner' name='QUESTIONNAIRE_NOT_COMPLETE' mdxType="Error" />
  <Error type='partner' name='CUSTOMER_NOT_READY_FOR_ENABLEMENT' mdxType="Error" />
  <Error type='partner' name='CUSTOMER_ALREADY_ENABLED' mdxType="Error" />
  <Error type='partner' name='CUSTOMER_ALREADY_CREATED' mdxType="Error" />
  <Error type='partner' name='LOGO_REQUIRED' mdxType="Error" />
  <Error type='partner' name='INVALID_LOGO' mdxType="Error" />
  <Error type='partner' name='CONTACT_REQUIRED' mdxType="Error" />
  <Error type='partner' name='ASSETS_UNDER_MANAGEMENT_REQUIRED' mdxType="Error" />
  <Error type='partner' name='CUSTOMER_REMOVAL_NOT_ALLOWED' mdxType="Error" />
  <Error type='partner' name='OAUTH_REGISTRATION_ERROR' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Check Report errors' subtitle='Occur when there is an error with creating or retrieving a Check Report.' columns={3}>
  <Error type='check-report' name='CONSUMER_REPORT_EXPIRED' mdxType="Error" />
  <Error type='check-report' name='CONSUMER_REPORT_NOT_FOUND' mdxType="Error" />
  <Error type='check-report' name='USER_ON_IDENTITY_THEFT_OR_FRAUD_BLOCK' mdxType="Error" />
  <Error type='check-report' name='USER_ON_SECURITY_FREEZE' mdxType="Error" />
  <Error type='check-report' name='DATA_UNAVAILABLE' mdxType="Error" />
  <Error type='check-report' name='PRODUCT_NOT_READY' mdxType="Error" />
  <Error type='check-report' name='INSTITUTION_TRANSACTION_HISTORY_NOT_SUPPORTED' mdxType="Error" />
  <Error type='check-report' name='INSUFFICIENT_TRANSACTION_DATA' mdxType="Error" />
  <Error type='check-report' name='NO_ACCOUNTS' mdxType="Error" />
  <Error type='check-report' name='INCOME_INSIGHTS_DELETED' mdxType="Error" />
  <Error type='check-report' name='NETWORK_CONSENT_REQUIRED' mdxType="Error" />
  <Error type='check-report' name='DATA_QUALITY_CHECK_FAILED' mdxType="Error" />
  <Error type='check-report' name='PRODUCT_NOT_REQUESTED' mdxType="Error" />
  <Error type='check-report' name='NO_MATCHING_USER' mdxType="Error" />
        </Table.Row>
        <Table.Row title='Check Report webhook errors' subtitle='Occur when a Check Report fails to generate and are returned on the Check Report failed webhooks.' columns={3}>
  <Error type='check-report-webhook' name='DATA_UNAVAILABLE' mdxType="Error" />
  <Error type='check-report-webhook' name='PRODUCT_GENERATION_FAILED' mdxType="Error" />
  <Error type='check-report-webhook' name='INTERNAL_SERVER_ERROR' mdxType="Error" />
        </Table.Row>
        <Table.Row title='CRA Monitoring errors' subtitle='Occur when there is an error creating or managing a Servicing subscription.' columns={3}>
  <Error type='cra-monitoring' name='SUBSCRIPTION_NOT_FOUND' mdxType="Error" />
  <Error type='cra-monitoring' name='SUBSCRIPTION_ALREADY_EXISTS' mdxType="Error" />
  <Error type='cra-monitoring' name='SUBSCRIPTION_LIMIT_EXCEEDED' mdxType="Error" />
        </Table.Row>
        <Table.Row title='User errors' subtitle='Occur when there is an error with creating or managing a user.' columns={3}>
  <Error type='user' name='USER_NOT_FOUND' mdxType="Error" />
        </Table.Row>
        <Table.Row></Table.Row>
      </Table.Container>
    </Layout.FullWidth>
    <h4 {...{
      "id": "error-schema"
    }}>{`Error schema`}</h4>
    <Schema model='PlaidError' type='model' tableOnly mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "most-common-errors",
        level: 4,
        title: "Most common errors",
      },
{
        id: "error-schema",
        level: 4,
        title: "Error schema",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
